// Step 1 of the booking flow — initial lead capture screen.
//
// New visual design (2026-05-25): centered title + copy, then a wide
// three-column row (Full Name · Email · Continue), promo opt-in centered
// below. Sits between the middle banner and the lower page parts.

import { useState } from 'react';
import styles from '../styles/widget.module.css';
import type { BookingAction, BookingState } from '../state/machine';

interface LeadCaptureProps {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  onContinue: () => void;
}

export function LeadCapture({ state, dispatch, onContinue }: LeadCaptureProps) {
  const [touched, setTouched] = useState(false);

  const canContinue =
    state.fullName.trim().length > 1 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email.trim());

  return (
    <div className={styles.leadCapture}>
      <div className={styles.stepHeader}>
        <h2 className={styles.heading}>Book Your Custom Fitting</h2>
        <p className={styles.leadCaptureCopy}>
          World’s #1 Custom Club Fitter
        </p>
      </div>
      <div className={styles.leadFields}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="cc-fullName">Name*</label>
          <input
            id="cc-fullName"
            className={styles.input}
            type="text"
            autoComplete="name"
            value={state.fullName}
            placeholder="What's your full name?"
            onChange={(e) => dispatch({ type: 'SET_LEAD', payload: { fullName: e.target.value } })}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="cc-email">Email*</label>
          <input
            id="cc-email"
            className={styles.input}
            type="email"
            autoComplete="email"
            value={state.email}
            placeholder="What is your email?"
            onChange={(e) => dispatch({ type: 'SET_LEAD', payload: { email: e.target.value } })}
          />
        </div>

        <button
          className={`${styles.button} ${styles.stepButtonMobile}`}
          disabled={!canContinue}
          data-cc-track="cta"
          data-cc-track-id="lead_capture_continue"
          onClick={() => {
            setTouched(true);
            if (canContinue) onContinue();
          }}
        >
          Continue
        </button>

        <div className={styles.leadOptin}>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={state.promoOptIn}
              onChange={(e) =>
                dispatch({ type: 'SET_LEAD', payload: { promoOptIn: e.target.checked } })
              }
            />
            <span>Contact me with offers</span>
          </label>
        </div>
      </div>

      {touched && !canContinue && (
        <div className={styles.error} style={{ maxWidth: 600, margin: '16px auto 0' }}>
          Fill in your name and a valid email to continue.
        </div>
      )}
    </div>
  );
}
